<script setup lang="ts">
/** Form A1 — Declaration for Register of Controllers and Register of Nominee Directors */
import { onMounted, computed } from 'vue';
import CddVarField from '../incorporation-pre/cdd/CddVarField.vue';
import { useCddFill } from '../incorporation-pre/cdd/useCddFill';
import SgPostSigners from './SgPostSigners.vue';
import { SG_POST_SIGNER_KEYS } from './sgPostSignersContext';
import './allotDoc.css';

type Loc = 'company_office' | 'filing_agent_office';
const LOCS: Loc[] = ['company_office', 'filing_agent_office'];

const fill = useCddFill();

/** Word: [[if rorc_location == "company_office"]] … [[if rorc_location == "filing_agent_office"]] */
function locOf(key: 'rorc_location' | 'rond_location'): Loc {
  const v = String(fill.values[key] ?? '').trim();
  return LOCS.includes(v as Loc) ? (v as Loc) : 'company_office';
}

function setLoc(key: 'rorc_location' | 'rond_location', v: Loc) {
  fill.setValue(key, v);
}

function mark(cur: Loc, v: Loc) {
  return cur === v ? '☑' : '☐';
}

const rorc = computed(() => locOf('rorc_location'));
const rond = computed(() => locOf('rond_location'));

onMounted(() => {
  if (!String(fill.values.rorc_location ?? '').trim()) fill.setValue('rorc_location', 'company_office');
  if (!String(fill.values.rond_location ?? '').trim()) fill.setValue('rond_location', 'company_office');
});
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-lbl-title" style="text-align:center; font-size:16px">FORM A1</div>
      <div class="doc-meta">
        Declaration for Register of Controllers and Register of Nominee Directors for Company<br />
        (This declaration relates to the Register of Controllers and Register of Nominee Directors to be kept by the company.)
      </div>
    </div>

    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Company Name</td>
          <td><CddVarField field-key="company_name" label="Company Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Company Registration No.</td>
          <td><CddVarField field-key="company_registration_no" label="Registration No." /></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p" style="margin-top:12px">
      I, <CddVarField field-key="declarant_name" label="Declarant Name" />
      (ID Card / Passport Number: <CddVarField field-key="declarant_identification_no" label="ID / Passport No." />),
      being a Director of the Company, hereby make the following declaration:
    </p>

    <p class="doc-lbl-title">Register of Controllers</p>
    <p class="doc-p">Please indicate where the Register of Controllers is kept:</p>
    <p class="doc-p">
      <span class="doc-toggle" @click="setLoc('rorc_location', 'company_office')">{{ mark(rorc, 'company_office') }} Registered office of the Company.</span>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="setLoc('rorc_location', 'filing_agent_office')">{{ mark(rorc, 'filing_agent_office') }} Registered office of a registered filing agent appointed by the Company for the purpose of keeping the Register of Controllers.</span>
    </p>

    <p class="doc-lbl-title">Register of Nominee Directors, if any</p>
    <p class="doc-p">Please indicate where the Register of Nominee Directors of the Company is kept:</p>
    <p class="doc-p">
      <span class="doc-toggle" @click="setLoc('rond_location', 'company_office')">{{ mark(rond, 'company_office') }} Registered office of the Company.</span>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="setLoc('rond_location', 'filing_agent_office')">{{ mark(rond, 'filing_agent_office') }} Registered office of a registered filing agent appointed by the Company for the purpose of keeping the Register of Nominee Directors.</span>
    </p>

    <p class="doc-p" style="margin-top:16px">
      Date: <CddVarField field-key="declaration_date" label="Declaration Date" />
    </p>
    <SgPostSigners :var-key="SG_POST_SIGNER_KEYS.formA1" board-label="DIRECTOR (DECLARANT) — select one" />
  </div>
</template>
